/**
 * catalogs.js — what to suggest: frecency (frequency + recency) learned from history.
 *
 * Restock model follows how kitchens run a daily prep/par sheet: recurring items bubble
 * up as suggestions (a learned par level) so the list rebuilds itself fast.
 */

import { BUILTIN_LISTS } from '../config/lists';
import { normalizeName } from './names';
import { localDateISO, daysBetween, timeMs } from './dates';
import { routeKey } from './lists';

/**
 * Frecency = frequency + recency (as in Firefox / zoxide / fre). One past
 * occurrence decays exponentially — today counts 1.0, one half-life ago 0.5, … —
 * so summed over the days an item appeared, frequent-AND-recent items score
 * highest. Half-life 30d (Mozilla's default); lower favours recency, higher frequency.
 */
const FRECENCY_HALF_LIFE_DAYS = 30;

function frecencyWeight(ageDays, halfLife = FRECENCY_HALF_LIFE_DAYS) {
  return Math.pow(0.5, Math.max(0, ageDays || 0) / halfLife);
}

// A Firestore Timestamp (or { seconds }, or ms) → ms; null = a server stamp still pending
// on this device, i.e. "just now" (later than anything already stamped).
const stampMs = v => {
  if (v === null) return Infinity;
  if (typeof v === 'number') return v;
  return v && typeof v.toMillis === 'function' ? v.toMillis() : timeMs(v);
};
// When a note was made: its server stamp; an old note without one, the start of its day.
const notedMs = it => (it.addedAt === undefined ? Date.parse(`${it.date}T00:00:00`) || 0 : stampMs(it.addedAt));

/** Sort comparator: highest frecency first, then most-recent, then A–Z. */
function byFrecency(a, b) {
  return (b.score - a.score)
    || (b.lastDate || '').localeCompare(a.lastDate || '')
    || a.name.localeCompare(b.name);
}

/**
 * Per-list picker catalogs for the Today board: every name ever noted in a list
 * (including today, so custom items are remembered) plus the seeds, scored by
 * frecency, best first. Frequency counts DISTINCT days; the freshest spelling wins;
 * unseen seeds score 0 so they trail learned items but keep the list non-empty.
 *
 * `lists` = the board's lists (built-ins by default); `route(name, fromId)` (logic/lists.js)
 * files each note — and seed — under the list its name lives in now, so an item moved
 * to another list takes its whole history of suggestions with it. `hidden` = names
 * removed from a list ({ "<listId>::<name>": when }): notes made up to then don't count
 * there, and its seed isn't offered — a new note brings the name back.
 * @returns { [listId]: [{ name, score, count, lastDate, fromHistory }] }
 */
export function buildCatalogs(items, todayISO, { lists = BUILTIN_LISTS, route, hidden = {} } = {}) {
  const today = todayISO || localDateISO();
  const perCat = {};
  lists.forEach(l => { perCat[l.id] = new Map(); });
  const home = (name, fromId) => (route ? route(name, fromId) : fromId);

  for (const it of items || []) {
    if (!it || !it.date) continue;
    const key = normalizeName(it.name);
    if (!key) continue;
    const dest = home(it.name, it.category);
    const bucket = perCat[dest];
    if (!bucket) continue;
    const cut = hidden[routeKey(dest, it.name)];
    if (cut !== undefined && notedMs(it) <= stampMs(cut)) continue;   // removed from this list since
    const prev = bucket.get(key) || { name: it.name, days: new Set(), lastDate: '' };
    prev.days.add(it.date);
    if (!prev.lastDate || it.date >= prev.lastDate) {
      prev.lastDate = it.date;
      prev.name = it.name;               // freshest spelling wins
    }
    bucket.set(key, prev);
  }

  // Seeds follow their routes too (a seed moved elsewhere seeds that list instead).
  const seedsFor = {};
  lists.forEach(l => (l.seeds || []).forEach(s => {
    const dest = home(s, l.id);
    if (perCat[dest] && hidden[routeKey(dest, s)] === undefined) (seedsFor[dest] = seedsFor[dest] || []).push(s);
  }));

  const out = {};
  for (const cat of lists) {
    const learned = [...perCat[cat.id].values()].map(e => {
      let score = 0;
      e.days.forEach(d => { score += frecencyWeight(daysBetween(d, today)); });
      return { name: e.name, score, count: e.days.size, lastDate: e.lastDate, fromHistory: true };
    });
    // Append seeds we haven't already learned, so the catalog is never empty.
    const seen = new Set(learned.map(e => normalizeName(e.name)));
    const seeds = [];
    (seedsFor[cat.id] || []).forEach(s => {
      const k = normalizeName(s);
      if (seen.has(k)) return;
      seen.add(k);
      seeds.push({ name: s, score: 0, count: 0, lastDate: '', fromHistory: false });
    });
    out[cat.id] = [...learned, ...seeds].sort(byFrecency);
  }
  return out;
}

/**
 * Frecency score per item (name → number) from the completion log — the same
 * decay the Today board uses, but over buys/cuts. Pending rows skipped; legacy
 * rows (no status) count as completed; a row with no date is treated as today.
 */
export function orderFrecency(log, todayISO) {
  const today = todayISO || localDateISO();
  const map = new Map();
  for (const l of log || []) {
    if (!l || (l.status || 'completed') === 'pending') continue;
    const key = normalizeName(l.name);
    if (!key) continue;
    map.set(key, (map.get(key) || 0) + frecencyWeight(daysBetween(l.date, today)));
  }
  return map;
}
